import { NgComponentOutlet } from '@angular/common';
import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	signal,
	type Type,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { select } from '@ngxs/store';
import { filter, firstValueFrom, take, timer } from 'rxjs';

import { UiFeatureFlagKey } from '@efficiently/feature-flags-core';
import { Account, AnalyticsEvent, OrgStatus, Permissions } from '@efficiently/models';

import { SettingsTypeEnum } from '@efficiently/client-api-application-settings';
import {
	AuthService,
	RegistrationRepository,
	SessionStateService,
	UserInformation,
} from '@efficiently/client-api-auth';
import { ClarityAdminRoleHint } from '@efficiently/client-api-clarity-session';
import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { AnalyticsService } from '@efficiently/client-core-analytics';
import { environment, isSandboxEnvironment } from '@efficiently/client-core-environment';
import { FeatureFlagsService } from '@efficiently/client-core-feature-flags';
import { ClientLoggerFacadeService } from '@efficiently/client-core-logger';
import { APP_ROUTES, PAGES_ROUTES } from '@efficiently/client-core-routing';
import { AppConfigService } from '@efficiently/client-core-services';
import { CoreSelectors } from '@efficiently/client-domain-core';
import { NavigationUtil } from '@efficiently/client-domain-navigation';
import { OrgAdminSelectors } from '@efficiently/client-domain-org-admin';
import { PermissionSelectors } from '@efficiently/client-domain-permissions';
import { ShowTooltipIfTruncatedDirective } from '@efficiently/client-ui-common';
import { EffIconComponent } from '@efficiently/client-ui-icons';
import { EffButtonComponent } from '@efficiently/client-ui-kit';
import {
	CLARITY_ADMIN_ROUTE_SEGMENT,
	CLARITY_INTEGRATIONS_ROUTE_SEGMENT,
	CLARITY_MCP_SETTINGS_ROUTE_SEGMENT,
	CLARITY_ORGANIZATION_ROUTE_SEGMENT,
	CLARITY_TASKS_ROUTE_SEGMENT,
	toClarityDomainNavigation,
} from '@efficiently/client-util-clarity';
import { AppPageLabelsEnum, SubscriptionState } from '@efficiently/client-util-classes';
import { SnackbarService } from '@efficiently/client-widget-snackbar';

// DEMO ONLY — DEVOPS-746, travels with the menu entries below. There is deliberately NO import of
// the prototype itself here — see `openFeatureFlagsDemo`.
import { AuthClient } from '@ds/core/enums/auth-client.enum';
import { ChargeBeePortalService } from '@ds/core/services/charge-bee/charge-bee-portal.service';
import { PLATFORM_ADMIN_LABEL } from '@ds/shared/constants/platform-admin-tabs.constant';
import type { PlatformAdminTabDefinition } from '@ds/shared/models/platform-admin-tab-definition.model';
import { ApplicationSettingsContainerComponent } from '@ds/shared/modules/application-settings/application-settings-container.component';
import { UpgradePlanDialogComponent } from '@ds/shared/modules/upgrade-plan-dialog';
import { permittedPlatformAdminTabs } from '@ds/shared/utils/platform-admin-tabs.util';
import { toUserDisplayName } from '@ds/shared/utils/user-display-name.util';

import { hasBillingAccess } from './has-billing-access.util';
import { MyProfileComponent } from './my-profile';
import { OrganizationMenuBridgeService } from './organization-menu-bridge.service';
import { SystemDomainMenuBridgeService } from './system-domain-menu-bridge.service';

/**
 * How long the page waits before reloading itself once a trial or expired org has finished paying —
 * the portal needs a moment to settle before the reloaded bundle reads the new subscription.
 */
const PAGE_RELOAD_TIME = 5000;

const BILLING_PROCESSING_SNACKBAR_DURATION = 10000;

/** DEMO ONLY — DEVOPS-746. The flag both demo variants are gated on. */
const DEMO_FLAG_KEY = UiFeatureFlagKey.SpecItem;

/** The leading characters of a commit hash the revision link shows. */
const COMMIT_ID_DISPLAY_LENGTH = 7;

/**
 * The user entry point of the platform shell, projected into the rail's footer slot (EFF-26081), and
 * with the reader's name (`withName`) into the domain navigator's footer, which covers the rail while
 * it is open. Both instances are live at once, so nothing here may be a once-per-app side effect: the
 * subscription renewal prompt is `SubscriptionRenewalPromptService`, provided once by the shell.
 *
 * It is the whole of what used to sit in the header's top-right corner — the identity, the status
 * indicators, the org and profile entries, and Log Out — re-homed into one popover that opens
 * upward from the bottom-left. Nothing was dropped in the move, and every entry keeps the
 * navigation, dialog, portal and analytics behaviour it had in the header.
 *
 * **Why one template rather than a tree of small ones.** `MatMenu` collects its rows through
 * `@ContentChildren(MatMenuItem)`, and a content query does not reach into a child component's own
 * view. Rows extracted into sub-components would render but never join the menu's key manager, so
 * arrow navigation would skip them — which is precisely AC 9. Every `mat-menu-item` therefore stays
 * in this template by contract, not by omission.
 *
 * **Why signals rather than the `ngOnInit` snapshots the old menu took.** The header menu was built
 * once per page load; this one lives in a shell that outlives every navigation, so a permission or
 * a subscription that changes mid-session has to reach the menu. The values that genuinely cannot
 * change within a session — the deployment and the build's commit — stay plain
 * readonly fields, because dressing a constant as a signal only hides which is which.
 */
@Component({
	selector: 'ds-user-menu',
	templateUrl: './user-menu.component.html',
	styleUrl: './user-menu.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	// The menu PANEL renders into the CDK overlay container at the document root, outside this
	// component's DOM, so an emulated-encapsulation attribute never reaches it. Every selector in the
	// stylesheet is a `ds-user-menu`-prefixed name that exists nowhere else.
	encapsulation: ViewEncapsulation.None,
	imports: [
		EffButtonComponent,
		EffIconComponent,
		MatDivider,
		// DEMO ONLY — DEVOPS-746. How the template renders a prototype it is not allowed to name.
		NgComponentOutlet,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		MatMenuTrigger,
		MatTooltip,
		RouterLink,
		ShowTooltipIfTruncatedDirective,
		TestIdDirective,
	],
})
export class UserMenuComponent {
	/** Draws the reader's name beside the initials trigger, as the domain navigator's footer does; the rail shows the initials alone. */
	public readonly withName = input(false, { transform: booleanAttribute });

	private readonly analyticsService = inject(AnalyticsService);
	private readonly appConfigService = inject(AppConfigService);
	private readonly authService = inject(AuthService);
	private readonly chargeBeePortalService = inject(ChargeBeePortalService);
	private readonly clarityAdminRoleHint = inject(ClarityAdminRoleHint);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	// DEMO ONLY — DEVOPS-746, for variant 2 below.
	private readonly features = inject(FeatureFlagsService);
	private readonly logger = inject(ClientLoggerFacadeService);
	private readonly organizationMenuBridge = inject(OrganizationMenuBridgeService);
	private readonly registrationRepository = inject(RegistrationRepository);
	private readonly router = inject(Router);
	private readonly sessionState = inject(SessionStateService);
	private readonly snackbarService = inject(SnackbarService);

	private readonly account = select<Account>(OrgAdminSelectors.account);
	private readonly globalPermissions = select<Permissions[]>(PermissionSelectors.globalPermissions);
	private readonly subscriptionState = select<SubscriptionState>(CoreSelectors.subscriptionState);
	private readonly userInfo = select<UserInformation>(CoreSelectors.userInfo);

	/**
	 * The Platform Administrator sub-tabs this caller may see, in strip order.
	 *
	 * The same function the section's own host builds its strip from, so the entry can never open a
	 * section whose strip would be empty — which is what makes the union below a consequence rather
	 * than a second list to keep in step (EFF-24841 AC 1).
	 */
	private readonly platformAdminTabs = computed<readonly PlatformAdminTabDefinition[]>(() =>
		permittedPlatformAdminTabs(this.globalPermissions()),
	);

	/**
	 * The trial CTA reads the SESSION's support flag while the banner below reads the one carried on
	 * the user record. They are two different reads in the header today — the wrapper asked
	 * `SessionStateService`, the menu asked `userInfo` — and the move keeps both rather than picking
	 * one, which would be a behaviour change no acceptance criterion asks for.
	 */
	private readonly isSupportSession = this.sessionState.isSupportLoggedIn();

	/** DEMO ONLY — DEVOPS-746, variant 2's supersession token — see `syncGatedFragment`. */
	private gatedFragmentSyncToken = 0;

	protected readonly initials = computed<string>(() => this.userInfo()?.name ?? '');
	protected readonly displayName = computed<string>(() => toUserDisplayName(this.userInfo()));
	protected readonly email = computed<string>(() => this.userInfo()?.contact?.email ?? '');
	/**
	 * The two halves the template renders side by side so a long address loses its MIDDLE.
	 *
	 * The split is at the LAST `@`, which is the semantic middle of an address: the head shrinks with
	 * an ellipsis and the domain arrives whole, so `alexandra.konstantinova@efficiently.com` reads as
	 * `alexandra.kons…@efficiently.com` rather than losing the part that says which company it is. An
	 * address with no `@` at all is not one this menu should silently mangle, so it stays in the head
	 * and truncates at its end like any other string.
	 */
	protected readonly emailHead = computed<string>(() => {
		const email = this.email();
		const splitAt = email.lastIndexOf('@');

		return splitAt === -1 ? email : email.slice(0, splitAt);
	});
	protected readonly emailTail = computed<string>(() => {
		const email = this.email();
		const splitAt = email.lastIndexOf('@');

		return splitAt === -1 ? '' : email.slice(splitAt);
	});
	protected readonly accountName = computed<string>(() => this.account()?.name ?? '');
	/** Shown only for a support session, and only when the record carries a contact — as in the header. */
	protected readonly isSupportBannerVisible = computed<boolean>(
		() => Boolean(this.userInfo()?.isSupportLoggedIn) && Boolean(this.userInfo()?.contact),
	);
	protected readonly triggerLabel = computed<string>(() => {
		const initials = this.initials();

		// The initials are the trigger's visible text, so they belong in its accessible name —
		// WCAG 2.5.3 Label in Name.
		return initials ? `${initials}, open user menu` : 'Open user menu';
	});

	protected readonly isBillingEnabled = computed<boolean>(() =>
		hasBillingAccess(this.globalPermissions(), this.appConfigService.config.billingEnabled),
	);
	protected readonly isAccountsAndContactsVisible = computed<boolean>(() =>
		this.hasPermission(Permissions.AccountsAndContactsTabView),
	);
	/** AC 1 — the union of the four gates: one permitted page is enough, none hides the entry. */
	protected readonly isPlatformAdminVisible = computed<boolean>(() => this.platformAdminTabs().length > 0);
	/**
	 * Where the entry navigates: the FIRST page the caller may see, not a fixed landing.
	 *
	 * A fixed link to Catalog would send a reader who may only see Organizations to a route their
	 * own `canMatch` guard refuses, and the shell would answer with the wildcard redirect to
	 * Projects — a menu entry that visibly does the wrong thing. The list this reads from applies
	 * the route's gate as well as the entry's, so the page it picks is one the guard will admit.
	 * Guarded by {@link isPlatformAdminVisible} in the template, so the empty case is never
	 * rendered.
	 */
	protected readonly platformAdminRoute = computed<readonly string[]>(
		() => this.platformAdminTabs()[0]?.routerLink ?? [],
	);
	/**
	 * The Clarity SYS-admin page (EFF-26277): shown to a Clarity admin (Brain's `role = 'admin'`).
	 *
	 * The role is Clarity's, not a platform permission, and the Clarity session lives below this
	 * shell — so it arrives through `ClarityAdminRoleHint`, and the entry appears once a Clarity
	 * session has been restored in this tab. The page's own guard applies the same condition, and
	 * Brain refuses every call the page makes to a non-admin.
	 */
	protected readonly isClarityAdminVisible = this.clarityAdminRoleHint.isAdmin;
	/**
	 * The Clarity organization administration panel (EFF-26575, BR-1): shown to a reader holding an
	 * `admin` or `billing` grant on at least one organization.
	 *
	 * The grant is Clarity's, not a platform permission, and the read that answers it lives below this
	 * shell — so it arrives through {@link OrganizationMenuBridgeService}, filled by
	 * `provideClarityOrganizationMenuLink()` on the `/clarity` subtree, and the entry appears once a
	 * Clarity session has been restored in this tab (TR-9). It is independent of
	 * {@link isClarityAdminVisible}: the SYS admin role and an organization grant are different things,
	 * and a reader can hold either without the other. The page's own guard applies the same condition,
	 * and brain refuses every call the page makes without a grant.
	 */
	protected readonly isOrganizationVisible = this.organizationMenuBridge.hasAccess;
	/** AC 4 — a group gated down to nothing renders neither its heading nor its divider. */
	protected readonly isOrgGroupVisible = this.isAccountsAndContactsVisible;
	protected readonly isTrialCtaVisible = computed<boolean>(
		() => !this.isSupportSession && this.subscriptionState()?.status === OrgStatus.Trial,
	);
	/**
	 * Whether the block between the identity and the `ORG` group has anything in it.
	 *
	 * It exists for the DIVIDER alone. Every row below gates itself, but the rule that each group
	 * opens with a divider would otherwise stack two of them whenever this block is empty and `ORG`
	 * follows — so the divider is bound to the group rather than to the position.
	 */
	protected readonly isStatusGroupVisible = computed<boolean>(
		() =>
			this.isSupportBannerVisible() ||
			this.isUpgradeEnabled ||
			this.isTrialCtaVisible() ||
			Boolean(this.commitId),
	);

	/**
	 * DEMO ONLY — DEVOPS-746. Whether this caller may see the walkthrough at all.
	 *
	 * Sandbox AND permission: `FlagEntitlementView` is held by `Platform Admin` alone, and the sandbox
	 * half keeps the entry off production for every role. Only the permission half is re-stated on the
	 * server (`FeatureFlagsDemoResolver`). Both halves are session constants, so this is a plain field.
	 */
	protected readonly isDemoEnabled = isSandboxEnvironment() && this.hasPermission(Permissions.FlagEntitlementView);

	/**
	 * DEMO ONLY — DEVOPS-746, variant 2. The prototype component as a VALUE — `null` until the flag is
	 * on and its chunk has arrived, and back to `null` the moment the flag goes off.
	 *
	 * A value, because the template may not NAME the class: a tag is a static reference, and a static
	 * reference from a component in the initial bundle would put the whole prototype there too.
	 * `NgComponentOutlet` renders what this holds without the template knowing what it is.
	 */
	protected readonly gatedFragment = signal<Type<unknown> | null>(null);

	protected readonly isUpgradeEnabled = Boolean(this.appConfigService.config.subscriptionState?.upgrade?.settingId);
	protected readonly commitId = isSandboxEnvironment() ? (environment?.build?.commitId ?? '') : '';
	protected readonly shortCommitId = this.commitId.substring(0, COMMIT_ID_DISPLAY_LENGTH);
	protected readonly commitUrl = `https://github.com/Efficiently-Dev/efficiently/commit/${this.commitId}`;

	protected readonly platformAdminLabel = PLATFORM_ADMIN_LABEL;
	protected readonly accountsAndContactsLabel = AppPageLabelsEnum.accountsAndContacts;
	/**
	 * Ungated, like the Control Center entry in the section switcher: `/clarity` is behind the platform
	 * session alone, and an unauthenticated Clarity session is answered by that section's own guard.
	 */
	protected readonly scheduleRoute = `/${APP_ROUTES.CLARITY.path}/${CLARITY_TASKS_ROUTE_SEGMENT}`;
	/**
	 * The *MCP Tools* entry — brain's own row of `UserProfileDropdown`, immediately before Integrations
	 * (`UserProfileDropdown.tsx:186-196`). Ungated there and ungated here, for the same reason
	 * {@link scheduleRoute} is: `/clarity` is behind the platform session alone, and an unauthenticated
	 * Clarity session is answered by that section's own guard.
	 */
	protected readonly mcpSettingsRoute = `/${APP_ROUTES.CLARITY.path}/${CLARITY_MCP_SETTINGS_ROUTE_SEGMENT}`;
	/** Ungated for the same reason {@link scheduleRoute} is — see its doc block. */
	protected readonly integrationsRoute = `/${APP_ROUTES.CLARITY.path}/${CLARITY_INTEGRATIONS_ROUTE_SEGMENT}`;
	/** Gated by {@link isClarityAdminVisible} in the template. */
	protected readonly clarityAdminRoute = `/${APP_ROUTES.CLARITY.path}/${CLARITY_ADMIN_ROUTE_SEGMENT}`;
	/** Gated by {@link isOrganizationVisible} in the template. */
	protected readonly organizationRoute = `/${APP_ROUTES.CLARITY.path}/${CLARITY_ORGANIZATION_ROUTE_SEGMENT}`;
	/** Ported from Clarity `apps/web/src/shared/ui/UserProfileDropdown.tsx` + `apps/web/src/app/pages/ClarityHomePage.tsx` (commit `f9217bb5a`). */
	/**
	 * The *System Domain* entry — Clarity's admin-only row of their `UserProfileDropdown`, last in `GENERAL`
	 * (the System Domain port, ticket 12 §4). Gated, unlike the two rows above, and not on a platform
	 * permission: the bridge holds an entry only while the reader is inside `/clarity`, their Clarity role is
	 * exactly `admin` and the System Domain is recognised.
	 */
	protected readonly systemDomainEntry = inject(SystemDomainMenuBridgeService).entry;
	protected readonly accountsAndContactsRoute = PAGES_ROUTES.ACCOUNTS_AND_CONTACTS.fullPath();
	protected readonly emptyQuery = NavigationUtil.emptyQuery();

	constructor() {
		// DEMO ONLY — DEVOPS-746, variant 2. An effect and not a one-time read: the flag arrives by a
		// socket push, so this both fetches the chunk when it turns on and DROPS the component when it
		// turns off. `@defer (when …)` cannot do the second half — `ɵɵdeferWhen` never triggers again
		// after Complete.
		const isDemoFlagEnabled = this.features.enabled(DEMO_FLAG_KEY);

		effect(() => {
			void this.syncGatedFragment(isDemoFlagEnabled());
		});
	}

	protected openMyProfile(): void {
		this.analyticsService.track(AnalyticsEvent.GoToMyProfile);
		this.dialog.open(MyProfileComponent, {
			autoFocus: false,
			width: '475px',
			height: '100%',
			position: { right: '0' },
			panelClass: 'ds-dialog-sidebar',
		});
	}

	protected openMySettings(): void {
		this.analyticsService.track(AnalyticsEvent.GoToMySettings);
		this.dialog.open(ApplicationSettingsContainerComponent, {
			width: '1230px',
			height: '100%',
			position: { right: '0' },
			panelClass: 'ds-dialog-sidebar',
			data: { isSidebar: true, settingsType: SettingsTypeEnum.mySetting },
		});
	}

	/**
	 * The `Billing` entry: the portal, reported.
	 *
	 * The report is HERE and not in {@link openBilling} because in the header only this entry fired
	 * `GoToBilling` — the trial CTA was the wrapper's own button and called the portal directly,
	 * untracked. Merging the two components must not merge that: a CTA click is an activation, not a
	 * visit to billing management, and folding it in would quietly change what the metric counts.
	 */
	protected openBillingFromMenu(): void {
		this.analyticsService.track(AnalyticsEvent.GoToBilling);
		this.openBilling();
	}

	/** The portal flow itself — what the trial CTA opens, with no analytics of its own. */
	protected openBilling(): void {
		this.chargeBeePortalService
			.openBilling((isAllowReload: boolean) => this.onBillingPortalClosed(isAllowReload))
			.pipe(take(1))
			.subscribe();
	}

	protected async openUpgradePlanDialog(): Promise<void> {
		try {
			const settingId = this.appConfigService.config.subscriptionState?.upgrade?.settingId;
			const data = await firstValueFrom(
				this.registrationRepository.getRegistrationContext(null, null, settingId).pipe(filter(Boolean)),
			);

			this.dialog.open(UpgradePlanDialogComponent, {
				width: '1210px',
				maxHeight: '95vh',
				panelClass: 'ds-dialog--dark',
				data: { price: data?.estimate?.userRate },
			});
		} catch (error) {
			// The dialog cannot price the plan without this context, so nothing opens and the person sees
			// no change — the silent-failure case the client logger exists for.
			this.logger.error('billing.upgrade_context_load_failed', error);
		}
	}

	protected logout(): void {
		this.analyticsService.track(AnalyticsEvent.LogOut);
		// The hint is root-scoped and the platform logout is an in-app navigation, so it would
		// otherwise outlive this user and show the Clarity admin entry to whoever signs in next.
		this.clarityAdminRoleHint.set(false);
		// Root-scoped for the same reason, and nothing re-runs the access read on the way out: the
		// `/clarity` subtree's injector — and the link that fills this — is gone by the time the next
		// user signs in, so a grant this user held would be offered to them (BR-1, logging out hides it).
		this.organizationMenuBridge.set(false);
		this.authService.logout();
	}

	/**
	 * Clarity's `goToDomain(systemDomainId)`, as their menu row calls it: a push to
	 * `/clarity/d/<systemDomainId>/pulse` that drops `doc`, `page`, `file`, `oi`, `action` and the legacy
	 * `domain` / `tab` keys and carries every other one — every `sd_*` included — and no navigation at all
	 * when that URL is where the reader already is.
	 */
	protected openSystemDomain(systemDomainId: string): void {
		const navigation = toClarityDomainNavigation(this.router.url, APP_ROUTES.CLARITY.path, systemDomainId);

		if (navigation !== null) {
			void this.router.navigateByUrl(navigation.url, { replaceUrl: navigation.replaceUrl });
		}
	}

	/**
	 * DEMO ONLY — DEVOPS-746. The walkthrough panel, and the one place the prototype is reached.
	 *
	 * `await import(...)` rather than a top-level import, and that is the whole isolation: chunk
	 * membership is decided at build time by module reachability, so a static reference from this
	 * component — which ships in the initial bundle with the shell — would put the prototype there too,
	 * no matter that `@if` hides the entry. The shape is refused mechanically: the base ESLint configs
	 * ban `@efficiently/client-prototype-*` as a static specifier at error severity
	 * (docs/common/codeguides/prototype-isolation.md § Client), so this is the only door that lints.
	 *
	 * `apolloClientKey` is passed in because the prototype may not import app code; `AuthClient` is the
	 * app's enum. No analytics event: the walkthrough is temporary and `AnalyticsEvent` is a shared
	 * contract.
	 */
	protected async openFeatureFlagsDemo(): Promise<void> {
		const { FeatureFlagsDemoComponent, FEATURE_FLAGS_DEMO_DIALOG_WIDTH } = await import(
			'@efficiently/client-prototype-feature-flags-demo'
		);

		this.dialog.open(FeatureFlagsDemoComponent, {
			autoFocus: false,
			width: FEATURE_FLAGS_DEMO_DIALOG_WIDTH,
			data: { apolloClientKey: AuthClient.Identity },
		});
	}

	/**
	 * DEMO ONLY — DEVOPS-746, variant 2's loader. The same one door as variant 1, `await import(...)`,
	 * reached from a different place: the dialog needs the class when a person clicks, this needs it
	 * when the flag says so.
	 *
	 * The token guards the off-while-fetching race: a socket push can turn the flag off while the chunk
	 * is still in flight, and the late resolve must not put the fragment back on screen. Each call takes
	 * a new token; a resolve that no longer holds the latest one has been superseded and does nothing.
	 */
	private async syncGatedFragment(isEnabled: boolean): Promise<void> {
		const token = ++this.gatedFragmentSyncToken;

		// Gated here rather than on the template `@if` so an ineligible caller never fetches the chunk.
		if (!isEnabled || !this.isDemoEnabled) {
			this.gatedFragment.set(null);

			return;
		}

		const { GatedMenuNoteComponent } = await import('@efficiently/client-prototype-feature-flags-demo');

		if (token !== this.gatedFragmentSyncToken) {
			return;
		}

		this.gatedFragment.set(GatedMenuNoteComponent);
	}

	private hasPermission(permission: Permissions): boolean {
		return this.globalPermissions()?.includes(permission) ?? false;
	}

	/**
	 * A trial or an expired org has a different bundle after paying, so the page reloads itself once
	 * the portal says the change landed. Anything else is still settling upstream, and the person is
	 * told to wait rather than shown a page that has not changed.
	 */
	private onBillingPortalClosed(isAllowReload: boolean): void {
		if (!isAllowReload) {
			this.snackbarService.processing(
				'Please wait subscription apply processing... It may take a few minutes. Thank you for your patience',
				{ duration: BILLING_PROCESSING_SNACKBAR_DURATION },
			);

			return;
		}

		const status = this.subscriptionState()?.status;

		if (status === OrgStatus.Trial || status === OrgStatus.Expired) {
			// Torn down with the shell: a reload fired after the session has moved on is a navigation the
			// person did not ask for.
			timer(PAGE_RELOAD_TIME)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => window.location.reload());
		}
	}
}
